import { useMutation, useQueryClient } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { useStepUp } from '@/auth/StepUp';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';

import {
  claveAlumno,
  resumenBorrado,
  TIPOS_VISTAS,
  type AlumnoDetalle,
  type HistorialBorrado,
  type TipoVistas,
} from './queries';

type Que = 'vistas' | 'simuladores' | 'examenes';

const CONFIRMACION: Record<Que, { titulo: string; descripcion: (tipo: TipoVistas) => string }> = {
  vistas: {
    titulo: 'Borrar historial de vistas',
    descripcion: (tipo) =>
      `Se borra el historial de ${tipo === 'todos' ? 'todos los tipos de contenido' : `«${TIPOS_VISTAS[tipo]}»`}: vistas, avance y casos contestados. ${
        tipo === 'todos' || tipo === 'clases'
          ? 'En clases también se borran las respuestas, las reseñas y el contador de reproducciones (se reinicia el límite). '
          : ''
      }La cuenta, las licencias, los pagos y las evaluaciones no se tocan. No se puede deshacer.`,
  },
  simuladores: {
    titulo: 'Borrar historial de simuladores',
    descripcion: () =>
      'Se borran todos los simuladores del alumno (en curso, pausados y terminados, con sus puntajes) y su histórico de la plataforma anterior. La cuenta, las licencias y los exámenes no se tocan. No se puede deshacer.',
  },
  examenes: {
    titulo: 'Borrar historial de exámenes',
    descripcion: () =>
      'Se borran todos los exámenes del alumno (en curso, pausados y terminados, con sus puntajes) y su histórico de la plataforma anterior. La cuenta, las licencias y los simuladores no se tocan. No se puede deshacer.',
  },
};

/** Borra historial del alumno sin borrar la cuenta (exige step-up y queda en la auditoría). */
export function BorrarHistorial({ a }: { a: AlumnoDetalle }) {
  const qc = useQueryClient();
  const { proteger, dialogo } = useStepUp();
  const [tipo, setTipo] = useState<TipoVistas>('todos');
  const [confirmar, setConfirmar] = useState<Que | null>(null);
  const path = { id: String(a.id) };

  const borrarM = useMutation({
    mutationFn: (que: Que) =>
      proteger(() =>
        unwrap<HistorialBorrado>(
          que === 'vistas'
            ? api.DELETE('/api/v1/admin/users/{id}/history/views', {
                params: { path, query: { type: tipo } },
              })
            : que === 'simuladores'
              ? api.DELETE('/api/v1/admin/users/{id}/history/simulators', { params: { path } })
              : api.DELETE('/api/v1/admin/users/{id}/history/exams', { params: { path } }),
        ),
      ),
    onSuccess: (r) => {
      setConfirmar(null);
      const { titulo, detalle } = resumenBorrado(r);
      toast.success(titulo, { description: detalle });
      void qc.invalidateQueries({ queryKey: claveAlumno(a.id) });
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });

  return (
    <section className="panel-vidrio space-y-4 border border-rojo-600/40">
      <h2 className="flex items-center gap-2 text-lg font-extrabold text-rojo-texto">
        <History size={20} aria-hidden /> Borrar historial
      </h2>
      <p className="text-texto-2">
        Reinicia el avance del alumno sin borrar su cuenta ni sus licencias. Te pediremos confirmar
        tu identidad.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-60 flex-1">
          <Campo etiqueta="Historial de vistas">
            <select
              className="input-vidrio"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoVistas)}
            >
              {Object.entries(TIPOS_VISTAS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </Campo>
        </div>
        <button type="button" className="btn-cs btn-peligro" onClick={() => setConfirmar('vistas')}>
          Borrar vistas
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-cs btn-contorno"
          onClick={() => setConfirmar('simuladores')}
        >
          Borrar historial de simuladores
        </button>
        <button
          type="button"
          className="btn-cs btn-contorno"
          onClick={() => setConfirmar('examenes')}
        >
          Borrar historial de exámenes
        </button>
      </div>
      <ConfirmarAccion
        key={confirmar ?? 'cerrado'}
        abierto={confirmar !== null}
        onCerrar={() => setConfirmar(null)}
        titulo={confirmar ? `${CONFIRMACION[confirmar].titulo} de ${a.name}` : ''}
        descripcion={confirmar ? CONFIRMACION[confirmar].descripcion(tipo) : ''}
        textoBoton="Borrar"
        peligro
        ocupado={borrarM.isPending}
        onConfirmar={() => confirmar && borrarM.mutate(confirmar)}
      />
      {dialogo}
    </section>
  );
}
